import { BellFilled, ShareAltOutlined } from '@ant-design/icons';
import { Spin, Dropdown, Button, Input, Row, Col, Typography, Divider } from 'antd';
import { Switch } from 'antd';
import moment from 'moment';
import { memo, useCallback, useMemo, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import useState from 'use-react-state';
import { X } from '../../assets/icons/jsxIcons';
import theme from '../../constants/theme';
import { createBroadcast, getBroadcasts, readBroadcast } from '../../utils/api/broadcast';
import { getTeams } from '../../utils/api/team';
import { capitalizeFirstLetter } from '../../utils/file';
import { useRequest, useSocketListeners } from '../../utils/hooks';
import { useUser } from '../../utils/hooks/user';
import Inifinite from '../common/Infinite';
import { Pressable, Text } from '../theme';
import styles from './styles.module.sass';

const { Text: AntText } = Typography;

const Broadcasts = memo(function () {
  const infiniteMessagesRef = useRef();
  const [{ dropdownOpen, hasNew }, setState] = useState({
    dropdownOpen: false,
    hasNew: false
  });

  const toggle = () =>
    setState((s) => {
      s.dropdownOpen = !s.dropdownOpen;
      s.hasNew = false;
    });

  const { teamId, agentId } = useUser(({ team, id } = {}) => ({
    agentId: id,
    teamId: team?.id || team
  }));

  const onBroadcast = (broadcast) => {
    if (broadcast?.agent?.id !== agentId) {
      setState({ hasNew: true });
      toast(
        <>
          <h4 className='font-weight-bolder'>
            {broadcast.agent?.first_name} {broadcast.agent?.last_name}
          </h4>
          {broadcast.message}
        </>,
        {
          position: 'top-center',
          autoClose: broadcast.pinned ? false : 10000
        }
      );
    }
  };

  useSocketListeners({
    listeners: {
      [`Broadcast.created.${teamId}`]: onBroadcast,
      'Broadcast.created': onBroadcast
    }
  });

  const onClose = useCallback(
    async (id, index) => {
      try {
        await readBroadcast(id);
        infiniteMessagesRef.current.setState((s) => {
          s.data.data.splice(index, 1);
        });
      } catch (error) {
        console.log(error);
      }
    },
    [infiniteMessagesRef]
  );

  const queryParams = useMemo(() => [{ limit: 5 }], []);

  const RenderItem = useCallback(
    ({ item: { agent, message, createdAt, id }, index }) => (
      <>
        <div className='px-4' style={{ width: 224 }}>
          <Col className='p-3'>
            <Row className='justify-content-between align-center'>
              <div>
                <AntText className={styles.agentName}>
                  {agent.first_name} {agent.last_name}
                </AntText>
                <AntText className={styles.time}>
                  {moment(createdAt).format('DD/MM/YYYY hh:mm A')}
                </AntText>
              </div>
              <X onClick={() => onClose(id, index)} width={10} height={10} role='button' />
            </Row>
            <AntText className={styles.message + ' mt-2 text-wrap'}>{message}</AntText>
          </Col>
        </div>
        <Divider
          className='border-primary'
          style={{
            marginLeft: '10%',
            border: '0.5px solid #000',
            width: '80%'
          }}
        />
      </>
    ),
    [onClose]
  );

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={toggle}
      dropdownRender={() => (
        <div
          style={{
            width: 224,
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            borderRadius: '4px'
          }}>
          <div className={styles.broadcastHead}>
            <AntText className={styles.broadcastHeadText + ' px-2'}>Notification</AntText>
          </div>
          <Inifinite
            ref={infiniteMessagesRef}
            params={queryParams}
            RenderItem={RenderItem}
            name='notifications'
            style={{ height: 350 }}
            asyncRequest={getBroadcasts}
            className='scroller'
            disableEndReached={false}
            disableBeginReached={true}
            loadOnMount={dropdownOpen}
          />
        </div>
      )}
      trigger={['click']}
      placement='bottomRight'>
      <Button className='bg-white px-1 border-0'>
        {hasNew && <div className={styles.redDot} />}
        <BellFilled style={{ color: '#000', fontSize: '18px' }} />
      </Button>
    </Dropdown>
  );
});

export const CreateBroadcast = memo(function () {
  const [{ dropdownOpen, pinned, message, teams, isLoading }, setState] = useState({
    dropdownOpen: false,
    pinned: false,
    isLoading: false,
    message: '',
    teams: undefined
  });

  const toggle = () =>
    setState((s) => {
      s.dropdownOpen = !s.dropdownOpen;
    });

  const {
    state: { data: { data = [] } = {} }
  } = useRequest({ asyncRequest: getTeams, name: 'teams', loadOnMount: dropdownOpen }, [
    dropdownOpen
  ]);

  const options = useMemo(
    () => data.map(({ name, id }) => ({ value: id, label: capitalizeFirstLetter(name) })),
    [data]
  );

  const handleChange = useCallback(
    (values) =>
      setState({
        teams: (values?.map && values.map(({ value }) => value)) || [values.value]
      }),
    []
  );

  const onPin = useCallback((pinned) => setState({ pinned }));

  const onChangeInput = useCallback(({ target: { value: message } }) => setState({ message }));

  const onCreateBroadcast = async () => {
    try {
      setState({ isLoading: true });
      await createBroadcast({ message, pinned, teams });
      setState({
        dropdownOpen: false,
        isLoading: false,
        pinned: false,
        message: '',
        teams: undefined
      });
    } catch (error) {
      console.log(error);
      toggle();
    }
  };

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={toggle}
      dropdownRender={() => (
        <div
          style={{
            width: 240,
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            borderRadius: '4px'
          }}>
          <div className={styles.broadcastHead}>
            <AntText className={styles.broadcastHeadText + ' px-2'}>Broadcast Message</AntText>
          </div>
          <div className='p-3'>
            <AntText className={styles.broadcastLabel}>Share a message with your agent</AntText>
            <Input.TextArea
              className='border-primary'
              name='message'
              value={message}
              style={{ height: 200, width: '100%', marginBottom: '12px' }}
              onChange={onChangeInput}
            />
            <AntText className='py-1'>Team</AntText>
            <Select
              styles={{
                control: (styles) => ({
                  ...styles,
                  borderColor: theme.colors.primary
                })
              }}
              dropdownIndicator={null}
              className='border-primary mb-3'
              onChange={handleChange}
              options={options}
              isMulti
            />
            <Row className='items-center mb-3'>
              <Switch checked={pinned} onChange={onPin} style={{ marginRight: 8 }} />
              <AntText>Pin</AntText>
            </Row>
            <Row justify='space-around' align='middle'>
              <Button onClick={toggle} className='mr-2'>
                Cancel
              </Button>
              <Button type='primary' onClick={onCreateBroadcast} loading={isLoading}>
                Broadcast
              </Button>
            </Row>
          </div>
        </div>
      )}
      trigger={['click']}
      placement='bottomRight'>
      <Button className='bg-white px-1 border-0'>
        <ShareAltOutlined style={{ color: '#000', fontSize: '18px' }} />
      </Button>
    </Dropdown>
  );
});

export default Broadcasts;
